/**
 * Ficha de un rol: qué puede hacer, qué no, y qué registros ve.
 *
 * Se lee como una ficha y no como una tabla de códigos, que es exactamente lo
 * que se pidió: primero la frase en castellano («dar de alta y editar Facturas
 * de cliente»), y el nombre técnico del modelo al costado, chico, para quien lo
 * necesite.
 *
 * El «qué NO puede» no está inventado: sale de comparar este rol contra el
 * universo de modelos que aparecen en TODOS los roles que devolvió el endpoint.
 * Si el endpoint no informa un módulo, acá no aparece — y eso es correcto, porque
 * afirmar «no puede tocar sueldos» cuando ni siquiera sabemos si el módulo existe
 * sería mentir.
 */

import { Badge } from '@/components/shell/primitives';
import type { CrudVerb, Role, RoleArea, RoleCapability, RoleScope, RoleScopeGroup } from '@/panel/contract';
import { concedeAlgo, verbosConcedidos, verbosNegados } from '@/panel/contract';
import { Codigo, Etiqueta, Panel } from '@/panel/ui';
import { ETIQUETA_VERBO, TONO_VERBO, etiquetaDeArea, textoDeAlcance } from '@/panel/vocabulario';

export interface UniversoModelos {
  /** modelo -> etiqueta y área, tal como los vio el panel en la respuesta. */
  modelos: Map<string, { label: string; area: string }>;
  /** área -> etiqueta, cuando el backend la mandó. */
  areas: Map<string, string>;
}

function ordenarAreas(areas: RoleArea[], etiquetas: Map<string, string>): RoleArea[] {
  return [...areas].sort((a, b) =>
    etiquetaDeArea(a.key, a.label === '' ? etiquetas.get(a.key) : a.label).localeCompare(
      etiquetaDeArea(b.key, b.label === '' ? etiquetas.get(b.key) : b.label),
      'es',
    ),
  );
}

function nombreDeArea(area: RoleArea, etiquetas: Map<string, string>): string {
  return etiquetaDeArea(area.key, area.label === '' ? etiquetas.get(area.key) : area.label);
}

/** Una línea de «puede»: modelo + los verbos concedidos como pastillas. */
function FilaCapacidad({ capacidad }: { capacidad: RoleCapability }) {
  const concedidos = verbosConcedidos(capacidad);
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-line/60 py-2 last:border-b-0">
      <span className="min-w-0">
        <span className="text-sm">{capacidad.label}</span>
        {!capacidad.labelIsTechnical && <Codigo className="ml-2">{capacidad.model}</Codigo>}
      </span>
      <span className="flex flex-wrap gap-1">
        {concedidos.map((verbo) => (
          <Badge key={verbo} tone={TONO_VERBO[verbo]}>
            {ETIQUETA_VERBO[verbo]}
          </Badge>
        ))}
      </span>
    </li>
  );
}

/** Resumen de límites dentro de un área que el rol sí toca. */
function LimitesDelArea({ capacidades }: { capacidades: RoleCapability[] }) {
  // Se agrupa por verbo negado: «No puede eliminar: Facturas, Asientos» se lee
  // mucho mejor que repetir el mismo «no puede» modelo por modelo.
  const porVerbo = new Map<CrudVerb, string[]>();
  for (const capacidad of capacidades) {
    if (!concedeAlgo(capacidad)) continue;
    for (const verbo of verbosNegados(capacidad)) {
      const lista = porVerbo.get(verbo);
      if (lista === undefined) porVerbo.set(verbo, [capacidad.label]);
      else lista.push(capacidad.label);
    }
  }
  if (porVerbo.size === 0) return null;

  return (
    <ul className="mt-2 space-y-1">
      {[...porVerbo.entries()].map(([verbo, modelos]) => (
        <li key={verbo} className="text-xs text-fg-muted">
          <span className="font-medium text-fg">No puede {ETIQUETA_VERBO[verbo].toLowerCase()}:</span>{' '}
          {modelos.join(', ')}
        </li>
      ))}
    </ul>
  );
}

/**
 * Una regla suelta, dentro del bloque que dice cómo se combina con las demás.
 *
 * Se muestran DOS cosas y en este orden, que es la corrección de fondo: primero
 * el nombre que le puso el `security.py` («Sólo los gastos de mis edificios»),
 * que es castellano del oficio y está escrito al lado del dominio que se
 * aplica; debajo, en chico, la traducción literal del dominio («Sólo los
 * registros donde «Período → Edificio → …» es el usuario conectado»), que es
 * exacta y sirve para auditar. Al revés —como estaba— la única frase visible
 * era una cadena de campos técnicos que una administradora de consorcios no
 * puede evaluar, y la pregunta que tiene que contestar antes de dar el rol es
 * si un propietario ve o no los gastos de otro edificio.
 */
function FilaRegla({ regla }: { regla: RoleScope }) {
  const { texto, crudo } = textoDeAlcance(regla.description, regla.domain);
  const titulo = regla.rule !== '' && regla.rule !== '(sin nombre)' ? regla.rule : texto;
  const detalle = titulo === texto ? null : texto;

  return (
    <li className="border-b border-line/60 py-1.5 last:border-b-0 last:pb-0">
      <p className="text-sm">{titulo}</p>
      {detalle !== null && (
        <p className="mt-0.5 text-xs text-fg-muted">
          {crudo ? <Codigo>{detalle}</Codigo> : detalle}
        </p>
      )}
    </li>
  );
}

/**
 * Las reglas de UN modelo, con el conector entre bloques a la vista.
 *
 * Antes esto era una lista plana donde «Empresa — sólo las de tu organización»
 * y «Empresa — todos los registros del modelo» salían como dos renglones
 * seguidos, sin nada que dijera si se suman o si el segundo pisa al primero. Lo
 * único que lo insinuaba era el `title=` de un chip, o sea nada en celular.
 * Ahora el modelo aparece una vez, los dos bloques llevan su encabezado escrito
 * («Se cumple siempre, sin excepción» / «Y además, alcanza con cumplir una de
 * estas») y arriba va la frase que dice qué resulta de combinarlos. Los tres
 * textos los redacta el backend, que es quien conoce el motor.
 */
function BloqueAlcance({ grupo }: { grupo: RoleScopeGroup }) {
  return (
    <section className="rounded-md border border-line p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h4 className="text-sm font-semibold">{grupo.label}</h4>
        {grupo.hard.length > 0 && (
          <Badge tone="warning">Con restricción dura</Badge>
        )}
      </div>
      {grupo.effect !== '' && <p className="mt-1 text-xs text-fg-muted">{grupo.effect}</p>}

      {grupo.hard.length > 0 && (
        <div className="mt-2">
          <Etiqueta as="h5">{grupo.hardTitle === '' ? 'Reglas globales' : grupo.hardTitle}</Etiqueta>
          <ul>
            {grupo.hard.map((regla, indice) => (
              <FilaRegla key={`dura-${indice}`} regla={regla} />
            ))}
          </ul>
        </div>
      )}

      {grupo.group.length > 0 && (
        <div className="mt-2">
          <Etiqueta as="h5">
            {grupo.groupTitle === ''
              ? 'Reglas del rol'
              : grupo.hard.length === 0
                ? // Sin bloque duro arriba, un encabezado que empieza con «Y
                  // además» no tiene a qué sumarse.
                  grupo.groupTitle.replace(/^Y además, alcanza/, 'Alcanza')
                : grupo.groupTitle}
          </Etiqueta>
          <ul>
            {grupo.group.map((regla, indice) => (
              <FilaRegla key={`rol-${indice}`} regla={regla} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export function FichaRol({ rol, universo }: { rol: Role; universo: UniversoModelos }) {
  const areasConAcceso = ordenarAreas(
    rol.areas.filter((area) => area.capabilities.some(concedeAlgo)),
    universo.areas,
  );
  const clavesConAcceso = new Set(areasConAcceso.map((area) => area.key));

  // Áreas que existen en el sistema (porque otro rol las toca) y este rol no.
  const areasSinAcceso = [...new Set([...universo.modelos.values()].map((entrada) => entrada.area))]
    .filter((clave) => !clavesConAcceso.has(clave))
    .map((clave) => etiquetaDeArea(clave, universo.areas.get(clave)))
    .sort((a, b) => a.localeCompare(b, 'es'));

  return (
    <div className="space-y-4">
      <Panel>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold tracking-tight">{rol.name}</h2>
            <p className="mt-1">
              <Codigo>{rol.code}</Codigo>
            </p>
            {rol.description !== '' && <p className="mt-2 max-w-2xl text-sm text-fg-muted">{rol.description}</p>}
          </div>
          <div className="text-right">
            <p className="text-2xs font-semibold uppercase tracking-[0.08em] text-fg-faint">Lo tienen</p>
            <p className="text-2xl font-semibold tabular-nums">
              {rol.userCount === null ? '—' : rol.userCount}
            </p>
            <p className="text-xs text-fg-muted">
              {rol.userCount === null
                ? 'sin dato del servidor'
                : rol.userCount === 1
                  ? 'persona'
                  : 'personas'}
            </p>
          </div>
        </div>

        {/* La advertencia que faltaba. Todo lo que dice esta ficha —incluido
            «no abre Facturación»— describe el ROL. Una persona marcada como
            superusuaria corta antes de las dos capas y abre todo igual, así que
            leer esta ficha creyendo que describe a esa persona lleva a la
            conclusión exactamente opuesta a la verdad. El texto lo manda el
            backend (`bypass_note`) porque es una afirmación sobre el motor de
            seguridad, no una redacción de la pantalla. */}
        {rol.bypassNote !== '' && (
          <p className="mt-3 rounded-md border border-warning/40 bg-warning-soft px-3 py-2 text-xs">
            {rol.bypassNote}
          </p>
        )}
      </Panel>

      <Panel title="Qué puede hacer" subtitle="Agrupado por área del sistema">
        {areasConAcceso.length === 0 ? (
          <p className="text-sm text-fg-muted">
            Este rol no concede ninguna operación por sí solo. Sirve como complemento de otro.
          </p>
        ) : (
          <div className="space-y-5">
            {areasConAcceso.map((area) => {
              const utiles = area.capabilities.filter(concedeAlgo);
              return (
                <section key={area.key}>
                  <Etiqueta as="h4">
                    {nombreDeArea(area, universo.areas)} · {utiles.length}{' '}
                    {utiles.length === 1 ? 'cosa' : 'cosas'}
                  </Etiqueta>
                  <ul>
                    {utiles.map((capacidad) => (
                      <FilaCapacidad key={capacidad.model} capacidad={capacidad} />
                    ))}
                  </ul>
                  <LimitesDelArea capacidades={utiles} />
                </section>
              );
            })}
          </div>
        )}
      </Panel>

      {areasSinAcceso.length > 0 && (
        <Panel title="Qué no puede hacer" subtitle="Áreas del sistema que este rol no abre">
          <ul className="flex flex-wrap gap-1.5">
            {areasSinAcceso.map((nombre) => (
              <li key={nombre}>
                <Badge tone="neutral">{nombre}</Badge>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-fg-muted">
            Sin acceso quiere decir que no ve ni siquiera la lista. Los límites dentro de las áreas que sí
            abre están arriba, debajo de cada una.
          </p>
        </Panel>
      )}

      <Panel title="Qué registros ve" subtitle="Filtro fila por fila, además de los permisos de arriba">
        {rol.scopeGroups.length === 0 ? (
          <p className="text-sm text-fg-muted">
            El servidor no informó reglas de registro para este rol. Con los permisos de arriba, ve todas
            las filas que su organización tenga de esos modelos.
          </p>
        ) : (
          <div className="space-y-3">
            {rol.scopeGroups.map((grupo) => (
              <BloqueAlcance key={grupo.model} grupo={grupo} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
